import { Link, useParams } from "react-router-dom";
import { Layers3, Loader2, SlidersVertical } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import SongStudioTabs from "@/components/creator/SongStudioTabs";
import MixConsole from "@/components/creator/mix/MixConsole";
import SongTempo from "@/components/creator/mix/SongTempo";
import useMixDesk from "@/components/creator/mix/useMixDesk";

function NeedsParts({ songId }) {
  return (
    <div data-testid="mix-needs-parts" className="rounded-3xl border border-dashed border-white/12 bg-white/[0.02] p-8">
      <p className="font-display text-lg font-semibold">Split the song into parts first</p>
      <p className="mt-1.5 max-w-xl text-[13px] text-white/60">The Mix Desk works on the separated parts — vocals, drums, bass, keys, guitar and the rest. Split once (about a minute), then come back here to balance them.</p>
      <Link to={`/create/song/${songId}/stems`} data-testid="mix-go-split" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 px-6 py-3 font-display text-sm font-semibold text-[#05070f]">
        <Layers3 className="h-4 w-4" /> Go to Parts
      </Link>
    </div>
  );
}

/** Mix Desk — balance every part and your voice, master it, and render numbered WAV + MP3 mixes. */
export default function MixDeskPage() {
  const { id } = useParams();
  const desk = useMixDesk(id);
  const back = `/create/song/${id}`;
  if (desk.error) return <CreatorShell testId="mix-desk-page" back="/create/projects" title="Hmm, that song isn't here" subtitle={desk.error} />;
  if (!desk.data || !desk.settings) {
    return <CreatorShell testId="mix-desk-page" back={back}><p className="flex items-center gap-2 text-white/60"><Loader2 className="h-5 w-5 animate-spin" /> Opening the Mix Desk…</p></CreatorShell>;
  }
  const { song, set } = desk.data;
  return (
    <CreatorShell testId="mix-desk-page" back={back} spine="mix" spineDone={["write", "generate", "record"]} wide>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-200/90"><SlidersVertical className="h-3.5 w-3.5" /> Mix Desk</p>
          <h1 data-testid="mix-title" className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{song.title}</h1>
          <p className="mt-2 text-white/60">Balance every part and your voice, give it a finished sound, and render the final song.</p>
          <SongTempo song={song} />
        </div>
        <SongStudioTabs songId={id} />
      </div>
      {set ? <MixConsole key={set.id} desk={desk} /> : <NeedsParts songId={id} />}
    </CreatorShell>
  );
}
